import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ButtonComp,
  ModalCompAdvance,
  EventTypeTabs,
} from "../../../components";
import FilterFind from "../../../components/FilterFind";
import Message from "../../../components/MessageModal";
import { EventManagementHeader } from "../../../utils/TableHeader";
import AllTables from "../modules/AllCompanyPrefix";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import {
  useGetEventsQuery,
  useGetPastEventsQuery,
  useGetCurrentEventsQuery,
  useGetUpcomingEventsQuery,
} from "../../../store/admin/eventSlice";
import TablePagination from "../../../components/Table/Pagination";
import useEventActions from "./useEventActions";
import EventDetailsModal from "./modules/EventDetailsModal";
import EventSettings from "./EventSettings";
import { isPastEvent } from "../../../utils/eventUtils";

export default function EventCalendar() {
  // eslint-disable-next-line no-unused-vars
  const [pageName, setPageName] = useState("Event Lists");
  const [pageNumber, setPageNumber] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const [modalToggle, setModalToggle] = useState(false);
  const [modalPage] = useState(0);
  const [viewModalToggle, setViewModalToggle] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Main tab state (Event Lists vs Settings)
  const [mainTab, setMainTab] = useState("Event Lists");

  // New state for filtering and event type tabs
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [attendeeFilter, setAttendeeFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");

  // Main tabs array - PageTab expects array of objects with name and component
  const mainTabs = [
    {
      name: "Event Lists",
      component: null, // Will be rendered in renderContent
    },
    {
      name: "Settings",
      component: null, // Will be rendered in renderContent
    },
  ];

  // Debounce search term to prevent too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { removeEvent } = useEventActions();
  const [eventId, setEventId] = useState(null);

  // Query parameters based on active tab and filters
  const queryParams = useMemo(
    () => ({
      page: pageNumber,
      search: debouncedSearchTerm || undefined,
      attendees: attendeeFilter || undefined,
      event_type:
        activeTab !== "all" ? activeTab : eventTypeFilter || undefined,
    }),
    [
      pageNumber,
      debouncedSearchTerm,
      attendeeFilter,
      activeTab,
      eventTypeFilter,
    ]
  );

  // Use different queries based on active tab
  const allEventsQuery = useGetEventsQuery(
    activeTab === "all" ? queryParams : { skip: true }
  );
  const pastEventsQuery = useGetPastEventsQuery(
    activeTab === "past" ? queryParams : { skip: true }
  );
  const currentEventsQuery = useGetCurrentEventsQuery(
    activeTab === "current" ? queryParams : { skip: true }
  );
  const upcomingEventsQuery = useGetUpcomingEventsQuery(
    activeTab === "upcoming" ? queryParams : { skip: true }
  );

  // Get counts for tabs (using all events query for counts, without filters)
  const countsQuery = useGetEventsQuery({ page: 1 });

  // Get separate counts for each tab type
  const allCountsQuery = useGetEventsQuery({ page: 1, event_type: undefined });
  const pastCountsQuery = useGetPastEventsQuery({ page: 1 });
  const currentCountsQuery = useGetCurrentEventsQuery({ page: 1 });
  const upcomingCountsQuery = useGetUpcomingEventsQuery({ page: 1 });

  // Get the active query result
  const activeQuery = useMemo(() => {
    switch (activeTab) {
      case "past":
        return pastEventsQuery;
      case "current":
        return currentEventsQuery;
      case "upcoming":
        return upcomingEventsQuery;
      default:
        return allEventsQuery;
    }
  }, [
    activeTab,
    allEventsQuery,
    pastEventsQuery,
    currentEventsQuery,
    upcomingEventsQuery,
  ]);

  const { data = {}, error, isLoading } = activeQuery;
  const { isLoading: countsLoading } = countsQuery;

  // Check if any of the count queries are loading
  const anyCountsLoading =
    allCountsQuery.isLoading ||
    pastCountsQuery.isLoading ||
    currentCountsQuery.isLoading ||
    upcomingCountsQuery.isLoading;
  if (error) {
    console.log(error);
  }

  // Calculate tab counts based on actual filtered data from each query
  const tabCounts = useMemo(() => {
    if (anyCountsLoading) {
      return { all: 0, past: 0, current: 0, upcoming: 0, loading: true };
    }

    // Get counts from the dedicated count queries
    const allCount = allCountsQuery.data?.pagination?.total || 0;
    const pastCount = pastCountsQuery.data?.pagination?.total || 0;
    const currentCount = currentCountsQuery.data?.pagination?.total || 0;
    const upcomingCount = upcomingCountsQuery.data?.pagination?.total || 0;

    return {
      all: allCount,
      past: pastCount,
      current: currentCount,
      upcoming: upcomingCount,
      loading: false,
    };
  }, [
    anyCountsLoading,
    allCountsQuery.data?.pagination?.total,
    pastCountsQuery.data?.pagination?.total,
    currentCountsQuery.data?.pagination?.total,
    upcomingCountsQuery.data?.pagination?.total,
  ]);

  const { event_calenders = [], pagination } = useMemo(() => {
    const { event_calendars = [], pagination } = data;
    const perPage = pagination?.perPage || 5;
    const items = event_calendars?.map((item, index) => {
      return {
        ...item,
        serialNumber: index + 1 + (pageNumber - 1) * perPage,
      };
    });
    return { event_calenders: items, pagination };
  }, [data, pageNumber]);

  const handleDelete = () => {
    removeEvent(eventId);
    setModalToggle(false);
  };

  const useModal = [
    {
      name: "Suspend Modal",
      component: (
        <Message
          title={"Confirm"}
          desc={"Are you sure you want to disable this bank account?"}
          onBack={() => setModalToggle()}
          onProceed={handleDelete}
        />
      ),
    },
    {
      name: "View Event Modal",
      component: (
        <EventDetailsModal
          eventData={selectedEvent}
          onClose={() => setViewModalToggle(false)}
        />
      ),
    },
  ];
  // Dynamic table dropdown based on event type
  const getTableDropDown = useCallback(
    (eventData) => {
      const isPast = isPastEvent(eventData);

      const baseActions = [
        {
          name: "View",
          action: (data) => {
            setSelectedEvent(data);
            setViewModalToggle(true);
          },
        },
      ];

      // Only show edit for non-past events
      if (!isPast) {
        baseActions.push({
          name: "Edit",
          action: (data) => {
            navigate(`${location?.pathname}/${data?.id}`);
          },
        });
      }

      // Delete is always available
      baseActions.push({
        name: "Delete",
        action: (data) => {
          setEventId(data.id);
          setModalToggle(true);
        },
      });

      return baseActions;
    },
    [navigate, location?.pathname]
  );

  // Event handlers
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setPageNumber(1); // Reset to first page when changing tabs
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    // Don't reset page number immediately - let debounced search handle it
  }, []);

  // Reset page number when debounced search changes
  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearchTerm]);

  const handleAttendeeChange = useCallback((value) => {
    setAttendeeFilter(value);
    setPageNumber(1);
  }, []);

  const handleEventTypeChange = useCallback((value) => {
    setEventTypeFilter(value);
    setPageNumber(1);
  }, []);

  const renderContent = () => {
    switch (mainTab) {
      case "Settings":
        return <EventSettings />;
      case "Event Lists":
      default:
        return (
          <>
            <div data-tour="staff-events-list">
            <FilterFind
              title={pageName}
              searchValue={searchTerm}
              onSearchChange={handleSearchChange}
              showAttendeeFilter={true}
              attendeeType={attendeeFilter}
              onAttendeeChange={handleAttendeeChange}
              showEventTypeFilter={activeTab === "all"}
              eventType={eventTypeFilter}
              onEventTypeChange={handleEventTypeChange}
              isLoading={isLoading || anyCountsLoading}
            />
            </div>

            {/* Event Type Tabs */}
            <EventTypeTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              counts={tabCounts}
            />

            <div className="text-end mb-3" data-tour="staff-events-create">
              <ButtonComp
                btnText={"Create New"}
                onClick={() => navigate("/super_admin/event_calendar/create")}
                btnClassName="bg-primary1  py-2 px-5 rounded text-white border-0"
              />
            </div>
            <div className="py-1"></div>

            {/* Event Table */}
            {(isLoading || anyCountsLoading) ? (
              <TableSkeletonLoader rows={10} columns={7} />
            ) : (
              <AllTables
                isLoading={isLoading}
                tableData={event_calenders}
                tableHeader={EventManagementHeader(getTableDropDown)}
                emptyMessage={
                  activeTab === "all"
                    ? "Event lists is empty, your created events will appear here"
                    : `No ${activeTab} events found`
                }
                TableDropDown={getTableDropDown}
                onSubmit={() => navigate("/super_admin/event_calendar/create")}
                marginBottom="70px"
              />
            )}

            {event_calenders.length > 0 && (
              <TablePagination handlePageChange={setPageNumber} {...pagination} />
            )}
          </>
        );
    }
  };

  return (
    <section className=" px-lg-3  min-vh-100 pb-5 mb-5">
      {/* Main Tabs */}
      <div className="d-flex gap-4 mb-4 border-bottom">
        {mainTabs.map((tab) => (
          <div
            key={tab.name}
            className={`pb-2 pointer fw-bold transition-all ${
              mainTab === tab.name
                ? "text-primary1 border-bottom border-primary1 border-3"
                : "text-blacklight hover:text-black1"
            }`}
            onClick={() => setMainTab(tab.name)}
            style={{ fontSize: "16px" }}
          >
            {tab.name}
          </div>
        ))}
      </div>

      {renderContent()}

      <ModalCompAdvance
        modalBody={modalPage}
        show={modalToggle}
        arrayComp={useModal}
        handleClose={() => setModalToggle(false)}
        centered
        size={"md"}
      />
      <ModalCompAdvance
        modalBody={1}
        show={viewModalToggle}
        arrayComp={useModal}
        handleClose={() => setViewModalToggle(false)}
        centered
        size={"lg"}
        close={true}
      />
    </section>
  );
}
